/// <reference types="cordova-plugin-device" />

import React, { useEffect, useMemo, useState } from "react";
import screenfull from "screenfull";
import "./App.css";
import MainScreen from "./pages/MainScreen";
import SplashScreen from "./pages/SplashScreen";
import MyExercises from "./pages/MyExercises";
import { Route, Routes, useNavigate } from "react-router-dom";
import SettingsModal from "./components/modals/SettingsModal";
import { FullScreenContext, FullScreenState } from "./contexts/FullScreenContext";
import { ExerciserSDK, IExerciserSDK } from "ap-exerciser-sdk";
import { useSettingsContext } from "./contexts/SettingsContext";
import { initializeApSharedLib } from "./utils/initializeAPSL";
import useScreenInformation from "./hooks/useScreenInformation";
import { DeviceContext, DeviceInfo } from "./contexts/DeviceContext";
import { Box } from "@chakra-ui/react";
import { NetworkState, useNetwork } from "./providers/NetworkProvider";
import { NoNetworkModal } from "./components/modals/NoNetworkModal";
import { WordItem } from "ap-exerciser-sdk/models";
import { isMobile, isTablet } from "react-device-detect";
import CancelConfirmModal from "./components/modals/CancelConfirmModal";
import { VocabIntlProvider } from "./hooks/useVocabTranslation";
import { VocabWord } from "./VocabModel";
import { suspend } from "suspend-react";

const LANGUAGES = ["en", "fr"];

const appConfig = {
  appName: process.env.REACT_APP_NAME,
  skinsAppName: process.env.REACT_APP_SKINS_NAME,
  language: process.env.REACT_APP_LANGUAGE,
  contentManager: {
    projectName: process.env.REACT_APP_CONTENT_MANAGER_PROJECT_NAME,
    identity: {
      client_id: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_CLIENT_ID,
      client_secret: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_CLIENT_SECRET,
      scope: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_SCOPE,
      grant_type: process.env.REACT_APP_CONTENT_MANAGER_IDENTITY_GRANT_TYPE,
    },
    baseUrl: process.env.REACT_APP_CONTENT_MANAGER_BASE_URL,
  },
  sessionManager: {
    storageOption: process.env.REACT_APP_SESSION_MANAGER_STORAGE_OPTION,
    initStats: {
      sessionStats: {
        sessionExerciseCount: 0,
        sessionWordList: []
      }
    }
  },
  firebase: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  },
  PUQ: {
    geoFunctionUrl: process.env.REACT_APP_FIREBASE_GEO_FUNCTION,
  },
  apiConfig: {
    baseUrl: process.env.REACT_APP_API_CONFIG_BASE_URL,
  },
  ttsApiEndpoint: process.env.REACT_APP_TTS_API_ENDPOINT,
  analyticsPrefix: process.env.REACT_APP_ANALYTICS_PREFIX
};

function App({ setSDKReady }: { setSDKReady: (ready: boolean) => void }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [transData, setTransData] = useState<any>({});
  const { setSettingModalOpen, setProfileData, setAvatar, onSetSettings, setSkin } = useSettingsContext();
  const screenInfo = useScreenInformation();
  const [noNetwork, setNoNetwork] = useState(false);
  const { networkState } = useNetwork();
  const [confirmSignout, setConfirmSignout] = useState(false);
  const [confirmDeletion, setconfirmDeletion] = useState(false);
  const navigate = useNavigate();

  const loadContent = async (sdk: IExerciserSDK) => {
    const contentManager = sdk.getContentManager()
    const textsData = await sdk.getDataManager().getStaticTexts();
    const ariasData = await contentManager.getData("ariatexts", "");
    const data = [...textsData?.items, ...ariasData?.items];

    if (data && data.length > 0) {
      const stagingData = data.map((item: any) => ({
        id: item.data.slug.iv,
        label: item.data.label,
      }));

      const stagingConverted = Object.assign({});

      LANGUAGES.map((language) => {
        stagingConverted[language] = stagingData.reduce(
          (result: any, langItem: any) => {
            result[langItem.id] = langItem.label[language];

            return result;
          },
          {}
        );
      });

      setTransData(stagingConverted);
    }
  }

  const loadSettings = async (sdk: IExerciserSDK) => {
    const settings = await sdk.getDataManager().getSettings();
    if (settings) {
      onSetSettings(settings);
      if (settings.bg) {
        const skin = await sdk.getDataManager().getSkin(settings.bg);
        setSkin(skin);
      }
    }
  }

  const { sdk, mappedWordList, mappedIdWordList } = suspend(async () => new Promise<{
    sdk: IExerciserSDK,
    mappedWordList: Map<string, VocabWord[]>,
    mappedIdWordList: Map<string, VocabWord[]>
  }>((resolve) => {
    if (!appConfig.sessionManager.storageOption || appConfig.sessionManager.storageOption.length === 0) {
      appConfig.sessionManager.storageOption = "local"
    }

    const sdkInstance = new ExerciserSDK(appConfig, (instance: IExerciserSDK) => {
      if (instance) {
        const mappedWordList = new Map<string, VocabWord[]>();
        const mappedIdWordList = new Map<string, VocabWord[]>();
        instance.getDataManager().getWordList().then(data => {
          const wordList: WordItem[] = [];
          if (mappedWordList.size > 0) {
            mappedWordList.clear()
          }
          if (mappedIdWordList.size > 0) {
            mappedIdWordList.clear()
          }
          data.items.forEach(item => {
            const word = new VocabWord(item);
            if (word.label && word.id) {
              wordList.push(word)
              const key = word.label.charAt(0);
              const keyId = word.id.charAt(0);

              if (!mappedWordList.has(key)) {
                mappedWordList.set(key, [])
              }
              mappedWordList.get(key)!.push(word)

              if (!mappedIdWordList.has(keyId)) {
                mappedIdWordList.set(keyId, [])
              }
              mappedIdWordList.get(keyId)!.push(word)

            } else {
              console.log("WordError", word)
            }
          })
          mappedWordList.forEach(a => {
              a.sort((el1: VocabWord, el2: VocabWord) => el1.label.localeCompare(el2.label));
            }
          )
          mappedIdWordList.forEach(a => {
              a.sort((el1: VocabWord, el2: VocabWord) => el1.id.localeCompare(el2.id));
            }
          )

          loadContent(instance).then(() => {
            loadSettings(instance).then(() => {
              setSDKReady(true);
              resolve({ sdk: instance, mappedWordList, mappedIdWordList })
            })
          })
        })
      }
    });

    initializeApSharedLib(appConfig.language, sdkInstance);
  }), ["sdk"])

  const exitHandler = (event: any) => {
    if (
      !document.fullscreen &&
      !(document as any).webkitIsFullScreen &&
      !(document as any).mozFullScreen &&
      !(document as any).msFullscreenElement
    ) {
      event.preventDefault();
      screenfull.exit();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("webkitfullscreenchange", exitHandler, false);
    document.addEventListener("mozfullscreenchange", exitHandler, false);
    document.addEventListener("fullscreenchange", exitHandler, false);
    document.addEventListener("MSFullscreenChange", exitHandler, false);

    return () => {
      document.removeEventListener("webkitfullscreenchange", exitHandler);
      document.removeEventListener("mozfullscreenchange", exitHandler);
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("MSFullscreenChange", exitHandler);
    };
  }, []);

  let state: FullScreenState = {
    fullScreen: fullscreen,
    toggleFullScreen: () => {
      if (!fullscreen) {
        screenfull.request(document.body);
      } else {
        screenfull.exit();
      }
      setFullscreen(!fullscreen);
    },
  };

  const deviceInfo: DeviceInfo = useMemo(() => {
    const device = (window as any).device;
    let inBrowser = !(device && (device.platform === "Android" || device.platform === "iOS"))
    if (isMobile && !isTablet && !inBrowser) {
      (window.screen.orientation as any)?.lock?.("portrait")
    } else {
      (window.screen.orientation as any)?.unlock?.()
    }

    return {
      landscape: screenInfo.landscape,
      narrow: (screenInfo.landscape ? screenInfo.width / screenInfo.height : screenInfo.height / screenInfo.width) > 2,
      touchscreen: screenInfo.hasTouchScreen,
      width: screenInfo.width,
      height: screenInfo.height,
      inBrowser: inBrowser
    }
  }, [screenInfo]);

  useEffect(() => {
    if (networkState >= NetworkState.NONE) {
      setNoNetwork(true);
    } else {
      setNoNetwork(false);
    }
  }, [networkState]);

  const refreshApp = () => {
    setConfirmSignout(false);
    setSettingModalOpen(false);
    setconfirmDeletion(false);
    setProfileData(null);
    setAvatar("");
    setSkin("");
    sessionStorage.clear();
    navigate("/");
  }

  const signout = () => {
    sdk?._firebase.getUserAuth().auth.signOut().then(() => {
      refreshApp();
    })
  }

  const deleteAccount = () => {
    setSettingModalOpen(false);
    sdk!.getObserverAppInstance().trigger("userdeleteaccount:show", { emailLink: process.env.REACT_APP_WEB_APP_HOST_URL + "?email=" + sdk?.getFirebase().getUserAuth().email }).then((res: any) => {
        sdk!.getObserverAppInstance().attach("userdeleteaccount:done", async (obj: any) => {
          sdk!.getFirebase().getUserAuth().reload().catch(() => {
            setconfirmDeletion(true);
          })
        });
      }
    )
  }

  return (
    <FullScreenContext.Provider value={state}>
      <DeviceContext.Provider value={deviceInfo}>
        <VocabIntlProvider locale={"fr"} messages={transData["fr"]} logError={1}>
          <Box className={`app`}>
            <Routes>
              <Route path="/" element={<SplashScreen sdk={sdk} />} />
              <Route path="/game" element={<MainScreen sdk={sdk} mappedIdWordList={mappedIdWordList}
                                                       mappedWordList={mappedWordList} />} />
              {/* <Route path="/wordlist" element={<NewScenario sdk={sdk} />} /> */}
              <Route path="/myexercises"
                     element={<MyExercises sdk={sdk} mappedIdWordList={mappedIdWordList}
                                           mappedWordList={mappedWordList} />} />
            </Routes>
            <SettingsModal isLoggedIn={sdk?._firebase?.isLoggedIn() ?? false}
                           requestSignout={() => setConfirmSignout(true)}
                           requestAccountDeletion={deleteAccount} sdk={sdk} />

            <CancelConfirmModal onConfirm={signout} open={confirmSignout} onClose={() => { setConfirmSignout(false) }}
                                title={"signout"}
                                description={"signout-confirm-text"}
                                btnTitle={"signout-confirm-action"}
            />
            <CancelConfirmModal onConfirm={refreshApp} open={confirmDeletion} onClose={refreshApp}
                                title={"account-delete-success-title"}
                                description={"account-delete-success-description"}
                                btnTitle={"account-delete-success-continue"}
            />

            <NoNetworkModal isOpen={noNetwork} />

            {/* <Profile sdk={sdk} /> */}
          </Box>
        </VocabIntlProvider>
      </DeviceContext.Provider>
    </FullScreenContext.Provider>
  );
}

export default App;
